import {
  afterNextRender,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  Injector,
} from '@angular/core';

import { setCssPropertyValue } from '@blaze-shared/shared-util';

import { CloseAffordanceRegistry } from '../services/close-affordance-registry.service';

/**
 * Must match the `8px` gap baked into the first-paint `--blaze-close-affordance` fallback in
 * dialog/drawer styles, so the measured refinement can only ever shrink the reservation.
 */
export const CLOSE_BUTTON_GAP_PX = 8;

/**
 * Publishes `--blaze-close-affordance` on the host: the horizontal space the host must give up
 * (as `padding-inline-end`) to stay clear of the enclosing dialog/drawer close button. Resolves
 * to `0px` when the host does not intersect the button's vertical band, and stays dormant while
 * no registered dialog/drawer contains the host — ownership comes from the registry's
 * DOM-ancestry lookup, since DI cannot see across the slot template boundary.
 *
 * The initial measurement runs in pre-paint `afterNextRender` phases so the first painted frame
 * is already refined; a ResizeObserver keeps the value in sync afterwards.
 */
@Directive({
  selector: '[blazeAvoidCloseButton]',
})
export class AvoidCloseButtonDirective {
  private readonly closeAffordance = inject(CloseAffordanceRegistry);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly injector = inject(Injector);

  private resizeObserver?: ResizeObserver;
  private appliedValue?: string;

  constructor() {
    effect(() => {
      const closeButton = this.closeAffordance.closeButtonFor(this.elementRef.nativeElement);
      const buttonElement = closeButton?.() ?? null;
      if (!closeButton && this.appliedValue === undefined) {
        return;
      }
      afterNextRender(
        {
          earlyRead: () => this.measureAffordance(buttonElement),
          write: (value) => {
            this.applyAffordance(value);
            this.observeResize(buttonElement);
          },
        },
        { injector: this.injector },
      );
    });

    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
  }

  private measureAffordance(closeButton: HTMLElement | null): string {
    if (!closeButton) {
      return '0px';
    }
    // The host's border-box right edge is unaffected by its own padding-inline-end, so
    // re-measuring after applying the value yields the same result — no oscillation.
    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const buttonRect = closeButton.getBoundingClientRect();
    const intersectsButtonBand =
      hostRect.top < buttonRect.bottom && hostRect.bottom > buttonRect.top;
    const overlap = intersectsButtonBand
      ? Math.max(0, hostRect.right - buttonRect.left + CLOSE_BUTTON_GAP_PX)
      : 0;
    return `${overlap}px`;
  }

  private applyAffordance(value: string): void {
    if (this.appliedValue === value) {
      return;
    }
    this.appliedValue = value;
    setCssPropertyValue('--blaze-close-affordance', value, this.elementRef.nativeElement);
  }

  private observeResize(closeButton: HTMLElement | null): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver ??= new ResizeObserver(() => {
      const currentButton =
        this.closeAffordance.closeButtonFor(this.elementRef.nativeElement)?.() ?? null;
      this.applyAffordance(this.measureAffordance(currentButton));
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
    if (closeButton) {
      this.resizeObserver.observe(closeButton);
    }
  }
}
